/**
 * The pass — a festival laminate hanging on a lanyard.
 *
 * It swings with the pointer the way one does on a strap; the front is the
 * pass, and flipping it over lists what it covers. It comes straight out of
 * the firm's own rooms — events people recognise a laminate before they read a
 * headline.
 *
 * `ElfPassHero` is the treatment itself and takes all of its wording as props.
 * `ElfHeroPass` is the homepage's use of it (hero variant 3, "All-access
 * pass"); the Events page hangs its own next date on the same pass.
 */

import React, { useCallback, useEffect, useRef, useState, type ReactNode } from 'react';
import ElfHeroNav from './ElfHeroNav';
import useMagnetic from './useMagnetic';
import { useConsultOpener } from './elfConsult';
import elfLogo from '../../../../assets/elf/elfcircle-logo.png';
import roboHead from '../../../../assets/elf/robo-head-3d.png';
import { scrollPastHero } from './ElfTear';

const ACCESS: [string, string][] = [
  ['Marketing', 'Campaigns, press, playlists, media buys'],
  ['Web + GEO', 'Sites, stores, booking — and AI answers'],
  ['Events', 'The monthly circuit, in six markets'],
  ['Merch', 'Drops cut for the room'],
  ['Consultations', 'A straight conversation first'],
];

/** How far the pass swings, and how quickly it settles. */
const SWING_DEG = 13;
const EASE = 0.085;
/** Change in movement that counts as a shake, and the wait before the next one. */
const SHAKE_FORCE = 15;
const SHAKE_GAP_MS = 1100;

/** iOS gates motion behind a permission that can only be asked during a tap. */
type MotionCtor = typeof DeviceMotionEvent & {
  requestPermission?: () => Promise<'granted' | 'denied' | 'default'>;
};

export interface ElfPassHeroProps {
  /** The header the hook is bolted to. */
  nav: ReactNode;
  /** Optional picture behind the whole hero, under a shade in the panel colour. */
  backdrop?: string;
  /** Optional line above the eyebrow — a breadcrumb on inner pages. */
  above?: ReactNode;
  eyebrow: string;
  /** Headline, one entry per line. Wrap a phrase in `<em>` to accent it. */
  title: ReactNode[];
  lede: ReactNode;
  /** The button row under the lede. */
  actions: ReactNode;
  /** Wording woven into the left and right lanyard straps. */
  straps: [string, string];
  front: {
    /** The big word across the pass. */
    headline: string;
    /** Who or what the pass is for — two short lines read best. */
    holder: ReactNode;
    metaLabel: string;
    meta: string;
  };
  back: {
    head: string;
    /** Name and one-line detail. */
    rows: [string, string][];
  };
  /** What turning the pass over reveals, for the button's accessible name. */
  flipReveals: string;
}

export const ElfPassHero: React.FC<ElfPassHeroProps> = ({
  nav,
  backdrop,
  above,
  eyebrow,
  title,
  lede,
  actions,
  straps,
  front,
  back,
  flipReveals,
}) => {
  const frameRef = useRef<HTMLDivElement>(null);
  const hangRef = useRef<HTMLDivElement>(null);
  const targetRef = useRef(0);
  const [flipped, setFlipped] = useState(false);
  /** Shake state: the last reading, when we last flipped, and whether we listen. */
  const forceRef = useRef(0);
  const lastShakeRef = useRef(0);
  const listeningRef = useRef(false);

  /* --- the swing ----------------------------------------------------------- */
  useEffect(() => {
    const hang = hangRef.current;
    if (!hang) return;
    // Reduced motion halves the swing rather than pinning the pass still.
    const gentle = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches ?? false;
    const amp = gentle ? 0.5 : 1;
    const touch = window.matchMedia?.('(hover: none)').matches;

    let raf = 0;
    let angle = 0;
    const start = performance.now();
    const tick = (now: number) => {
      // Touch has no pointer to follow: the pass sways on its own instead.
      const target = (touch ? Math.sin((now - start) / 1400) * (SWING_DEG * 0.5) : targetRef.current) * amp;
      angle += (target - angle) * EASE;
      hang.style.transform = `rotate(${angle.toFixed(2)}deg)`;
      raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, []);

  const onMove = useCallback((e: React.PointerEvent<HTMLDivElement>) => {
    if (e.pointerType !== 'mouse') return;
    const frame = frameRef.current;
    if (!frame) return;
    const r = frame.getBoundingClientRect();
    targetRef.current = ((e.clientX - r.left) / r.width - 0.5) * 2 * SWING_DEG;
  }, []);

  const onLeave = useCallback(() => {
    targetRef.current = 0;
  }, []);

  /* --- shake to turn the pass over --------------------------------------- */

  /** Turns the pass and gives it a knock, as a real one would get. */
  const shaken = useCallback(() => {
    const now = Date.now();
    if (now - lastShakeRef.current < SHAKE_GAP_MS) return;
    lastShakeRef.current = now;
    setFlipped((v) => !v);
    targetRef.current = SWING_DEG * 1.6;
    window.setTimeout(() => {
      targetRef.current = 0;
    }, 420);
  }, []);

  const listen = useCallback(() => {
    if (listeningRef.current || typeof DeviceMotionEvent === 'undefined') return;
    listeningRef.current = true;
    window.addEventListener('devicemotion', (e: DeviceMotionEvent) => {
      const a = e.accelerationIncludingGravity;
      if (!a) return;
      const force = Math.hypot(a.x ?? 0, a.y ?? 0, a.z ?? 0);
      // Compare against the last reading: a shake is the change, not the value,
      // so holding the phone at any angle never counts.
      if (forceRef.current && Math.abs(force - forceRef.current) > SHAKE_FORCE) shaken();
      forceRef.current = force;
    });
  }, [shaken]);

  // Android and desktop browsers report motion straight away; iOS stays silent
  // until the permission below is granted, so this costs nothing there.
  useEffect(() => {
    if (window.matchMedia?.('(hover: none)').matches) listen();
  }, [listen]);

  /** Tapping the pass is also the gesture iOS needs to grant motion access. */
  const flip = useCallback(() => {
    setFlipped((v) => !v);
    const Motion = typeof DeviceMotionEvent === 'undefined' ? undefined : (DeviceMotionEvent as MotionCtor);
    if (Motion?.requestPermission) {
      Motion.requestPermission()
        .then((res) => {
          if (res === 'granted') listen();
        })
        .catch(() => {
          // Declined, or not served over https — tapping still turns the pass.
        });
    }
  }, [listen]);

  return (
    <div
      className={backdrop ? 'elf-hv elf-hv--pass elf-hv--backdrop' : 'elf-hv elf-hv--pass'}
      ref={frameRef}
      onPointerMove={onMove}
      onPointerLeave={onLeave}
    >
      {backdrop && (
        <>
          <img className="hvp__bg" src={backdrop} alt="" />
          <span className="hvp__shade" />
        </>
      )}
      {nav}

      <div className="hvp">
        <div className="hvp__copy">
          {above}
          <span className="eyebrow an" style={{ alignSelf: 'flex-start', animationDelay: '.2s' }}>
            <i />
            {eyebrow}
          </span>
          <h1 className="hvp__h1">
            {title.map((line, i) => (
              <span className="mask" key={i}>
                <span style={{ animationDelay: `${0.3 + i * 0.12}s` }}>{line}</span>
              </span>
            ))}
          </h1>
          <p className="hvp__lede an" style={{ animationDelay: '.66s' }}>
            {lede}
          </p>
          <div className="hvp__cta an" style={{ animationDelay: '.78s' }}>
            {actions}
          </div>
        </div>

        <div className="hvp__rig">
          {/* A wall hook screwed to the header band: mounting plate, two screws,
              and the arm the pass's ring hangs over. Drawn rather than boxed, so
              the curve and the metal read at any size. */}
          <svg className="hvp__hook" viewBox="0 0 120 150" aria-hidden="true">
            <defs>
              <linearGradient id="hvpSteel" x1="0" y1="0" x2="1" y2="0">
                <stop offset="0%" stopColor="#6d6d84" />
                <stop offset="28%" stopColor="#e8e8f3" />
                <stop offset="55%" stopColor="#b6b6c9" />
                <stop offset="100%" stopColor="#5c5c72" />
              </linearGradient>
              <linearGradient id="hvpPlate" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor="#e4e4ef" />
                <stop offset="60%" stopColor="#a8a8bd" />
                <stop offset="100%" stopColor="#70708a" />
              </linearGradient>
            </defs>
            {/* plate, bolted flat to the wall */}
            <rect x="46" y="2" width="60" height="30" rx="9" fill="url(#hvpPlate)" />
            <circle cx="60" cy="17" r="4.6" fill="#5b5b73" />
            <path d="M56.6 17h6.8" stroke="#33334a" strokeWidth="1.6" strokeLinecap="round" />
            <circle cx="92" cy="17" r="4.6" fill="#5b5b73" />
            <path d="M88.6 17h6.8" stroke="#33334a" strokeWidth="1.6" strokeLinecap="round" />
            {/* the arm: down from the plate, then curving up into the hook */}
            <path
              d="M76 30 V68 C76 106 42 114 38 88 C36 74 46 67 55 73"
              fill="none"
              stroke="url(#hvpSteel)"
              strokeWidth="12"
              strokeLinecap="round"
            />
            <path
              d="M76 34 V68 C76 101 47 109 43 89"
              fill="none"
              stroke="rgba(255,255,255,.55)"
              strokeWidth="3"
              strokeLinecap="round"
            />
          </svg>
          <div className="hvp__hang" ref={hangRef}>
            {/* The split ring the webbing is threaded through, drawn in the same
                steel as the hook. Its hole is open, so the hook's arm shows
                through it the way it would on a real one. */}
            <svg className="hvp__ring" viewBox="0 0 44 44" aria-hidden="true">
              <defs>
                <linearGradient id="hvpRingSteel" x1="0" y1="0" x2="1" y2="1">
                  <stop offset="0%" stopColor="#f4f4fa" />
                  <stop offset="40%" stopColor="#a6a6bd" />
                  <stop offset="72%" stopColor="#dedeeb" />
                  <stop offset="100%" stopColor="#5b5b73" />
                </linearGradient>
              </defs>
              <circle cx="22" cy="22" r="15" fill="none" stroke="url(#hvpRingSteel)" strokeWidth="6" />
              <circle
                cx="22"
                cy="22"
                r="15"
                fill="none"
                stroke="rgba(255,255,255,.65)"
                strokeWidth="1.6"
                strokeDasharray="16 80"
                strokeDashoffset="8"
              />
            </svg>
            <span className="hvp__strap hvp__strap--l" aria-hidden="true">
              <b>{straps[0]}</b>
            </span>
            <span className="hvp__strap hvp__strap--r" aria-hidden="true">
              <b>{straps[1]}</b>
            </span>
            <span className="hvp__clip" aria-hidden="true" />

            <button
              type="button"
              className="hvp__card"
              data-flipped={flipped ? 'true' : undefined}
              aria-pressed={flipped}
              aria-label={flipped ? 'Turn the pass back over' : `Turn the pass over to see ${flipReveals}`}
              onClick={flip}
            >
              <span className="hvp__face hvp__face--front">
                <span className="hvp__punch" aria-hidden="true" />
                {/* The mascot's head, mounted on the pass like a holder photo. */}
                <span className="hvp__badge">
                  <img src={roboHead} alt="ELF-CIRCLE mascot" />
                </span>
                <img className="hvp__crest" src={elfLogo} alt="" />
                <b className="hvp__all">{front.headline}</b>
                <span className="hvp__holder">{front.holder}</span>
                <span className="hvp__meta">
                  <i>{front.metaLabel}</i>
                  {front.meta}
                </span>
                <span className="hvp__bars" aria-hidden="true" />
                <span className="hvp__hint">
                  <span className="hint-hover">Tap to turn over</span>
                  <span className="hint-touch">Shake or tap to turn over</span>
                </span>
              </span>

              <span className="hvp__face hvp__face--back">
                <span className="hvp__punch" aria-hidden="true" />
                <b className="hvp__backhead">{back.head}</b>
                <ul className="hvp__list">
                  {back.rows.map(([name, what]) => (
                    <li key={name}>
                      <b>{name}</b>
                      <i>{what}</i>
                    </li>
                  ))}
                </ul>
                <span className="hvp__hint">
                  <span className="hint-hover">Tap to turn back</span>
                  <span className="hint-touch">Shake or tap to turn back</span>
                </span>
              </span>
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

/** Homepage hero variant 3 — "All-access pass". */
export const ElfHeroPass: React.FC = () => {
  const magnetic = useMagnetic();
  const bookConsult = useConsultOpener({ source: 'hero.pass', title: 'Book a consultation' });

  return (
    <ElfPassHero
      nav={<ElfHeroNav />}
      eyebrow="Access, not audience"
      title={[
        'An all-access pass',
        <>
          to the <em>ELF world.</em>
        </>,
      ]}
      lede="Most agencies buy their way into culture. ELF-CIRCLE has been inside it for thirty-five years — marketing, web development, events, merch and consultations, for artists, models and music creators."
      actions={
        <>
          <button type="button" className="cta mag" {...magnetic} onClick={bookConsult}>
            Book a Consultation
          </button>
          <button type="button" className="discover" onClick={scrollPastHero}>
            Discover <i>↗</i>
          </button>
        </>
      }
      straps={['ELF-CIRCLE · ELF-CIRCLE · ELF-CIRCLE ·', 'ALL AREAS · ALL AREAS · ALL AREAS ·']}
      front={{
        headline: 'All areas',
        holder: (
          <>
            Artists · Models
            <br />
            Music creators
          </>
        ),
        metaLabel: 'Season',
        meta: '2026 — 2027',
      }}
      back={{ head: 'Access covers', rows: ACCESS }}
      flipReveals="what access covers"
    />
  );
};

export default ElfHeroPass;
